import axios from 'axios';

import getAuthSession from './auth';
import { Questionnaire, QuestionnaireResultArray, ReportVersion } from '../interfaces/questionnaire.interface';
import { Domain } from '../interfaces/domain.interface';
import { WorkflowTypeKey } from '../interfaces/workflow.interface';

export async function getOnBoardingQuestionnaires(companyType: string) {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/questionnaires/?type=${companyType}&is_published=true`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QuestionnaireResultArray;
}

/** All questionnaires sharing a version (the version group). */
export async function fetchQuestionnairesByVersion(version: string) {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/questionnaires/?version=${encodeURIComponent(version)}&limit=50`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QuestionnaireResultArray;
}

export async function fetchQuestionnaireByTitle(title: string) {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/questionnaires/?title=${title}`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QuestionnaireResultArray;
}

// Mirrors assessments.constants.VerificationScopeChoices on the backend. Only the
// historical 5.3.0 questionnaire is verification-list scoped; every new questionnaire
// verifies all remediations.
export const VERIFICATION_SCOPES = {
  VERIFICATION_LIST: 'verification_list',
  ALL_REMEDIATIONS: 'all_remediations',
} as const;

/**
 * Report layout for a questionnaire version. Unknown (newer, dynamically created)
 * versions get the newest layout — never the oldest, which the previous
 * `find(...) ?? v5_1` fallbacks silently produced.
 */
export const resolveReportVersion = (version?: string): ReportVersion => {
  const match = Object.values(ReportVersion).find((known) => known === version);
  return match ?? ReportVersion.v5_3_1;
};

/**
 * Codename of the feature flag auto-created for each MPA BP questionnaire version,
 * mirroring assessments.models.questionnaire_feature_flag_codename on the backend.
 */
export const questionnaireFeatureFlagCodename = (version: string): string =>
  `can_use_mpa_best_practice_questionnaire_v${version.replace(/\./g, '_')}`;

export const QUESTIONNAIRE_VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

export const isValidQuestionnaireVersion = (version: string): boolean =>
  QUESTIONNAIRE_VERSION_PATTERN.test(version);

export enum QuestionnaireVersions {
  V_5_2_0 = '5.2.0',
  V_5_1_0 = '5.1.0',
  V_5_3_0 = '5.3.0',
  V_5_3_1 = '5.3.1',
}

export const QuestionnaireTypes = {
  SITE_BASELINE: 'site_baseline',
  APPLICATION_BASELINE: 'application_baseline',
  MPA_BEST_PRACTICE: 'mpa_best_practice',
};

export const questionnaireTypes = [
  { value: 'site_baseline', label: 'Site Baseline' },
  { value: 'application_baseline', label: 'Application Baseline' },
  { value: 'mpa_best_practice', label: 'TPN MPA Best Practices Questionnaire' },
];

// These gates compare against the frozen list of legacy workflow types so that new,
// dynamically created questionnaire versions automatically get the newest behavior.
const PRE_5_3_WORKFLOW_TYPES: string[] = ['tpn_best_practices_5.1', 'tpn_best_practices_5.2'];
const PRE_5_3_1_WORKFLOW_TYPES: string[] = [...PRE_5_3_WORKFLOW_TYPES, 'tpn_best_practices_5.3'];

export function isWorkflowFiveThreeOrGreater(wfType: WorkflowTypeKey | string): boolean {
  // Can be used for feature flags or conditional rendering
  return !!wfType && !PRE_5_3_WORKFLOW_TYPES.includes(wfType);
}

export function isWorkflowFiveThreeOneOrGreater(wfType: WorkflowTypeKey | string): boolean {
  return !!wfType && !PRE_5_3_1_WORKFLOW_TYPES.includes(wfType);
}

export function isWorkflowPreFiveThreeOne(wfType: WorkflowTypeKey | string): boolean {
  return PRE_5_3_1_WORKFLOW_TYPES.includes(wfType);
}

export async function promoteQuestionnaire(oldParent: number, newParent: number) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/promote-questionnaire-version/previous-parent/${oldParent}/new-parent/${newParent}/`,
    {},
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function versionQuestionnaire(questionnaireId: number) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/duplicate-questionnaire/${questionnaireId}/`,
    {},
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

/**
 * Queue duplication of a whole questionnaire version group (all 3 types) on the worker.
 * The requester is emailed when the duplication finishes.
 */
export async function duplicateQuestionnaireGroup(sourceVersion: string, targetVersion?: string) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/duplicate-questionnaire-group/`,
    { sourceVersion, targetVersion: targetVersion || '' },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

/**
 * Queue re-serialization of a questionnaire's stored content (the copy users download
 * when they open it). Runs on the worker; takes a minute or two.
 */
export async function serializeQuestionnaire(questionnaireId: number) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/questionnaires/${questionnaireId}/serialize/`,
    {},
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function deleteQuestionnaire(questionnaireSlug: string) {
  const authSession = await getAuthSession();
  const response = await axios.delete(
    `${process.env.REACT_APP_BASE_API}/questionnaires/${questionnaireSlug}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function fetchDetailedQuestionnaire(
  questionnaireId: number,
): Promise<Questionnaire> {
  const authSession = await getAuthSession();

  const response = await axios.get<Questionnaire>(
    `${process.env.REACT_APP_BASE_API}/questionnaires/${questionnaireId}/data/`,
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );

  return response.data;
}

export const buildFullQuestionnaireObject = async (questionnaireid: number) => {
  const authSession = await getAuthSession();
  return Promise.all([
    axios.get(
      `${process.env.REACT_APP_BASE_API}/domains/?questionnaire=${questionnaireid}&limit=200`,
      {
        headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
      },
    ),
  ]).then(res => {
    return { domains: res[0].data.results as Domain[] };
  });
};

export const fetchQuestionnaire = async (questionnaireSlug: string) => {
  const authSession = await getAuthSession();
  const response =  await axios.get(`${process.env.REACT_APP_BASE_API}/questionnaires/${questionnaireSlug}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as Questionnaire;
};

export const fetchTopics = async (topicSlug: string) => {
  const authSession = await getAuthSession();
  const response = await axios.get(`${process.env.REACT_APP_BASE_API}/domains/${topicSlug}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
};


export const fetchBestPractices = async (bpSlug?: string, queryParams?: {
  [key: string]: string | number;
}) => {
  const authSession = await getAuthSession();

  let url = `${process.env.REACT_APP_BASE_API}/best-practices/${bpSlug || ''}`;

  // Append query parameters if they exist and `bpSlug` is not provided
  if (!bpSlug && queryParams) {
    const queryString = new URLSearchParams(queryParams as Record<string, string>).toString();
    url += queryString ? `?${queryString}&limit=9999` : '';
  }

  const response = await axios.get(
    url,
    {
      headers: {
        'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data;
};

export const fetchQuestionnaires = async (questionnaireType: string) => {
  const authSession = await getAuthSession();
  const response = await axios.get(`${process.env.REACT_APP_BASE_API}/report-questionnaires/?type=${questionnaireType}`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return await response.data.results as Questionnaire[];
};
